<script setup lang="ts">
/**
 * Gift 01–04 Who (3222:17334 … 3222:17779; mobile 3222:105810 …). Saved people as Theme
 * cards, the new-person fields right under them — the same four fields as Add person on
 * People. Choosing a card clears the fields; typing in the fields clears the card.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Person } from '@/types/people'
import { FairDialog } from '@/ui/primitives/FairDialog'
import { FairThemeCard } from '@/ui/primitives/FairThemeCard'
import { FairToggleItem } from '@/ui/primitives/FairToggleItem'
import FairProfileV2RegionField from '@/widgets/profile/fair-v2/FairProfileV2RegionField.vue'
import FairProfileV2RegionList from '@/widgets/profile/fair-v2/FairProfileV2RegionList.vue'
import FairProfileV2TextField from '@/widgets/profile/fair-v2/FairProfileV2TextField.vue'
import {
  fairProfileV2AgeLabel,
  fairProfileV2GendersFor,
  fairProfileV2RelationLabel,
  withFairProfileV2Age,
  type FairProfileV2PeopleLists,
  type FairProfileV2PersonDraft,
  type FairProfileV2PersonGender,
} from '@/widgets/profile/fair-v2/fair-profile-v2-people.data'
import type { FairProfileV2RegionOption } from '@/widgets/profile/fair-v2/fair-profile-v2-settings.data'

/** Above the quiz layer (`--fair-rd-z-quiz: 1100`); PrimeVue's default overlay base (1000) is under it. */
const QUIZ_OVERLAY_BASE_Z_INDEX = 1200

const props = defineProps<{
  people: Person[]
  selectedId: number | null
  draft: FairProfileV2PersonDraft
  compact: boolean
  /** The gift definition's who lists (`GET /gift/config`); null while loading or when it has none. */
  lists: FairProfileV2PeopleLists | null
  /** The lists could not be loaded (or the definition has none): the fields say so. */
  listsUnavailable?: boolean
}>()

const emit = defineEmits<{
  'update:selectedId': [id: number | null]
  'update:draft': [draft: FairProfileV2PersonDraft]
}>()

const { t, te } = useI18n()

const relationOptions = computed<FairProfileV2RegionOption[]>(() => props.lists?.relations ?? [])
const ageOptions = computed<FairProfileV2RegionOption[]>(() => props.lists?.ageRanges ?? [])
const genders = computed(() => fairProfileV2GendersFor(props.lists, props.draft.age))

function legacyRelationLabel(key: string): string | null {
  const path = `profile.people_page.relation.${key}`
  return te(path) ? t(path) : null
}

function relationLabel(relation: string | null | undefined): string {
  return fairProfileV2RelationLabel(props.lists, relation, legacyRelationLabel)
}

function genderLabel(gender: FairProfileV2PersonGender): string {
  return props.lists?.genders.find((option) => option.id === gender)?.label ?? t(`profile.people_page.gender_value.${gender}`)
}

function choosePerson(id: number) {
  emit('update:selectedId', props.selectedId === id ? null : id)
  emit('update:draft', { name: '', relation: null, age: null, gender: null })
}

function edit(draft: FairProfileV2PersonDraft) {
  if (props.selectedId !== null) emit('update:selectedId', null)
  emit('update:draft', draft)
}

/** Mobile: Relation / Age open a sheet with the list (People `M 07 / 08`). */
const sheet = ref<'relation' | 'age' | null>(null)

function pickRelation(option: FairProfileV2RegionOption) {
  edit({ ...props.draft, relation: option.id })
  sheet.value = null
}

function pickAge(option: FairProfileV2RegionOption) {
  edit(withFairProfileV2Age(props.draft, option.id, props.lists))
  sheet.value = null
}
</script>

<template>
  <div class="gift-quiz-who">
    <section
      v-if="people.length"
      class="gift-quiz-who__group"
    >
      <h3 class="gift-quiz-who__label">
        {{ t('gift_quiz.who.saved') }}
      </h3>
      <div
        class="gift-quiz-who__cards"
        role="radiogroup"
        :aria-label="t('gift_quiz.who.saved')"
      >
        <FairThemeCard
          v-for="person in people"
          :key="person.id"
          :label="person.profileName"
          :description="relationLabel(person.relationship)"
          :selected="selectedId === person.id"
          @click="choosePerson(person.id)"
        />
      </div>
    </section>

    <section class="gift-quiz-who__group">
      <h3 class="gift-quiz-who__label">
        {{ people.length ? t('gift_quiz.who.add_new') : t('gift_quiz.who.new_person') }}
      </h3>
      <p
        v-if="listsUnavailable"
        class="gift-quiz-who__error"
        role="alert"
      >
        {{ t('gift_quiz.who.lists_error') }}
      </p>
      <div class="gift-quiz-who__form">
        <FairProfileV2TextField
          :model-value="draft.name"
          :label="t('profile.people_page.name')"
          autocomplete="off"
          :maxlength="100"
          @update:model-value="edit({ ...draft, name: $event })"
        />
        <FairProfileV2RegionField
          inline
          :searchable="false"
          :label="t('profile.people_page.relation_label')"
          :value="relationLabel(draft.relation)"
          :placeholder="t('profile.people_page.relation_label')"
          :compact="compact"
          :disabled="!lists"
          :overlay-base-z-index="QUIZ_OVERLAY_BASE_Z_INDEX"
          :options="relationOptions"
          :selected-id="draft.relation"
          empty-text=""
          @select="pickRelation"
          @open="sheet = 'relation'"
        />
        <FairProfileV2RegionField
          inline
          :searchable="false"
          :label="t('profile.people_page.age_label')"
          :value="fairProfileV2AgeLabel(lists, draft.age)"
          :placeholder="t('profile.people_page.age_label')"
          :compact="compact"
          :disabled="!lists"
          :overlay-base-z-index="QUIZ_OVERLAY_BASE_Z_INDEX"
          :options="ageOptions"
          :selected-id="draft.age"
          empty-text=""
          @select="pickAge"
          @open="sheet = 'age'"
        />
        <div
          class="gift-quiz-who__gender"
          role="radiogroup"
          :aria-label="t('profile.people_page.gender')"
        >
          <FairToggleItem
            v-for="gender in genders"
            :key="gender"
            :label="genderLabel(gender)"
            :selected="draft.gender === gender"
            @click="edit({ ...draft, gender: draft.gender === gender ? null : gender })"
          />
        </div>
      </div>
    </section>

    <FairDialog
      :visible="sheet !== null"
      size="mobile"
      :header="sheet === 'age' ? t('profile.people_page.age_label') : t('profile.people_page.relation_label')"
      :show-footer="false"
      :close-label="t('quiz.close')"
      @update:visible="(value) => !value && (sheet = null)"
    >
      <FairProfileV2RegionList
        :options="sheet === 'age' ? ageOptions : relationOptions"
        :selected-id="sheet === 'age' ? draft.age : draft.relation"
        :searchable="false"
        radios
        empty-text=""
        @select="sheet === 'age' ? pickAge($event) : pickRelation($event)"
      />
    </FairDialog>
  </div>
</template>
